"use client";

import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { cn } from "../../lib/utils";
import { Toaster } from "../common/Toast";

interface FixedBottomContextType {
  currentContent: ReactNode | null;
  setContent: (content: ReactNode, className: string) => void;
  clearContent: () => void;
}

const FixedBottomContext = createContext<FixedBottomContextType | null>(null);

function useKeyboardOffset() {
  const [offset, setOffset] = useState(0);

  const updateOffset = useCallback(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;

    const bottomOffset = window.innerHeight - (viewport.offsetTop + viewport.height);
    setOffset(Math.max(0, bottomOffset));
  }, []);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;

    viewport.addEventListener("resize", updateOffset);
    viewport.addEventListener("scroll", updateOffset);

    return () => {
      viewport.removeEventListener("resize", updateOffset);
      viewport.removeEventListener("scroll", updateOffset);
    };
  }, [updateOffset]);

  return offset;
}

function GradientBlurLayer({ height }: { height: number }) {
  return (
    <div
      className="pointer-events-none absolute inset-x-0 bottom-0 z-30"
      style={{
        height: `${height}px`,
        backdropFilter: "blur(100px)",
        WebkitBackdropFilter: "blur(100px)",
        maskImage: "linear-gradient(to bottom, transparent 0%, black 100%)",
        WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, black 100%)",
        background: "rgba(255, 255, 255, 0)",
      }}
    />
  );
}

interface FixedBottomLayoutProps {
  children: ReactNode;
  className?: string;
  hasBottomGap?: boolean;
  bottomGapOverride?: number;
  hasGradient?: boolean;
  hasGradientBlur?: boolean;
  enableKeyboardOffset?: boolean;
}

export function FixedBottomLayout({
  children,
  className,
  hasBottomGap = true,
  bottomGapOverride,
  hasGradient = false,
  hasGradientBlur = false,
  enableKeyboardOffset = false,
}: FixedBottomLayoutProps) {
  const [currentContent, setCurrentContent] = useState<ReactNode | null>(null);
  const [contentClassName, setContentClassName] = useState<string | null>(null);
  const [contentHeight, setContentHeight] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);
  const rawKeyboardOffset = useKeyboardOffset();
  const keyboardOffset = enableKeyboardOffset ? rawKeyboardOffset : 0;

  const setContent = (content: ReactNode, className: string) => {
    setCurrentContent(content);
    setContentClassName(className);
  };

  const clearContent = () => {
    setCurrentContent(null);
    setContentClassName(null);
  };

  useLayoutEffect(() => {
    if (currentContent && contentRef.current) {
      const updateHeight = () => {
        setContentHeight(contentRef.current?.offsetHeight || 0);
      };

      updateHeight();

      const resizeObserver = new ResizeObserver(updateHeight);
      resizeObserver.observe(contentRef.current);

      return () => resizeObserver.disconnect();
    }
    setContentHeight(0);
  }, [currentContent]);

  const resolvedBottomGap =
    typeof bottomGapOverride === "number" && Number.isFinite(bottomGapOverride)
      ? Math.max(0, bottomGapOverride)
      : hasBottomGap
        ? contentHeight
        : 0;

  return (
    <FixedBottomContext.Provider value={{ currentContent, setContent, clearContent }}>
      <div className={cn("relative", className)}>
        {children}
        <div
          style={{
            paddingBottom: resolvedBottomGap,
            transition: "padding-bottom 200ms ease-out",
          }}
        />

        {/* 토스트 알림 영역 */}
        <Toaster offset={contentHeight + 16} />

        {/* 고정 콘텐츠 영역 */}
        {currentContent && (
          <div
            ref={contentRef}
            className={cn(
              "fixed right-0 left-0 z-50",
              hasGradient || hasGradientBlur ? "bg-transparent" : "bg-white",
              "mx-auto max-w-[600px]",
              contentClassName,
            )}
            style={{
              bottom: `${keyboardOffset}px`,
              transition: keyboardOffset > 0 ? "none" : "bottom 200ms ease-out",
            }}
          >
            {hasGradient && (
              <>
                <div
                  className="pointer-events-none absolute inset-x-0 bottom-0 z-30 h-[100px]"
                  style={{
                    backgroundImage:
                      "linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 40%, rgba(255, 255, 255, 1) 100%)",
                    paddingBottom: "env(safe-area-inset-bottom)",
                  }}
                />
                <div
                  className="pointer-events-none absolute inset-x-0 bottom-0 z-20 bg-white"
                  style={{
                    height: "env(safe-area-inset-bottom)",
                  }}
                />
              </>
            )}
            {hasGradientBlur && <GradientBlurLayer height={contentHeight + 12} />}
            <div className="relative z-50 w-full pb-[env(safe-area-inset-bottom)]">
              {currentContent}
            </div>
          </div>
        )}
      </div>
    </FixedBottomContext.Provider>
  );
}

interface FixedBottomContentProps {
  children: ReactNode;
  className?: string;
}

export function FixedBottomContent({ children, className }: FixedBottomContentProps) {
  const context = useContext(FixedBottomContext);

  if (!context) {
    throw new Error("FixedBottomLayout.Content must be used within FixedBottomLayout");
  }

  const { setContent, clearContent } = context;

  useLayoutEffect(() => {
    setContent(children, className ?? "");

    return () => {
      clearContent();
    };
  }, [children, setContent, clearContent, className]);

  return null;
}
